import React, { useEffect, useState } from "react";
import { Briefcase, Plus, Users, Clock, Plane, GraduationCap, FileSignature, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHero, Section, StatCard, Badge, Button, Input, Select, Field, DataTable, EmptyState } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";
import { toast } from "sonner";

const TABS = [
  { key: "employees",   label: "Employees",   icon: Users },
  { key: "attendance",  label: "Attendance",  icon: Clock },
  { key: "leave",       label: "Leave",       icon: Plane },
  { key: "training",    label: "Training",    icon: GraduationCap },
  { key: "contracts",   label: "Contracts",   icon: FileSignature },
  { key: "performance", label: "Performance", icon: TrendingUp },
];

export default function HR() {
  const { has } = usePermissions();
  const canWrite = has("data:hr:write");
  const [tab, setTab] = useState("employees");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { api.get("/hr/dashboard").then(r => setStats(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/hr/${tab}`).then(r => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
    setShowForm(false); setForm({});
  }, [tab]);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post(`/hr/${tab === "attendance" ? "attendance" : tab}`, form);
      toast.success("Saved");
      const { data } = await api.get(`/hr/${tab}`);
      setRows(data || []);
      setShowForm(false); setForm({});
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6" data-testid="hr-page">
      <PageHero
        eyebrow="HR360"
        title="Human Resources"
        subtitle="Employees, attendance, leave, training, contracts and performance — everything about your team in one workspace."
        moduleKind="hr"
        actions={canWrite && <Button variant="primary" onClick={() => setShowForm(v => !v)} data-testid="hr-add-btn"><Plus className="w-4 h-4" /> Add {tab.replace(/s$/, "")}</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard label="Active employees" value={stats?.active_employees ?? 0} icon={Users} tone="info" testid="hr-stat-employees" />
        <StatCard label="On leave today" value={stats?.on_leave_today ?? 0} icon={Plane} tone="accent" testid="hr-stat-leave" />
        <StatCard label="Pending leave" value={stats?.pending_leave_requests ?? 0} icon={Clock} tone="muted" testid="hr-stat-pending-leave" />
        <StatCard label="Active training" value={stats?.active_training_programmes ?? 0} icon={GraduationCap} tone="success" testid="hr-stat-training" />
      </div>

      <Section className="relative overflow-hidden">
        <Pattern kind="hr" />
        <div className="relative">
          <div className="flex flex-wrap gap-1.5 mb-5 p-1 rounded-full border border-border bg-muted/40 w-fit">
            {TABS.map(t => (
              <button
                key={t.key}
                data-testid={`hr-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${tab === t.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {showForm && <HRForm tab={tab} form={form} setForm={setForm} submit={submit} onCancel={() => setShowForm(false)} />}

          {loading ? (
            <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-muted/40 rounded-lg" />)}</div>
          ) : rows.length === 0 ? (
            <EmptyState icon={Briefcase} title={`No ${tab} yet`} description={canWrite ? "Add the first record using the button above." : "Records will appear here once created."} />
          ) : (
            <DataTable
              testid={`hr-table-${tab}`}
              columns={hrColumns(tab)}
              rows={rows}
            />
          )}
        </div>
      </Section>
    </div>
  );
}

function hrColumns(tab) {
  switch (tab) {
    case "employees":
      return [
        { header: "Name", cell: r => <span className="font-medium">{r.full_name}</span> },
        { header: "Role", key: "role_title" },
        { header: "Department", key: "department" },
        { header: "Contract", cell: r => <Badge variant="primary">{r.contract_type}</Badge> },
        { header: "Hire date", key: "hire_date" },
        { header: "Status", cell: r => <Badge variant={r.status === "active" ? "healthy" : "draft"}>{r.status}</Badge> },
      ];
    case "attendance":
      return [
        { header: "Employee", key: "employee_id" },
        { header: "Date", key: "date" },
        { header: "Status", cell: r => <Badge variant={r.status === "present" ? "healthy" : r.status === "absent" ? "critical" : "pending"}>{r.status}</Badge> },
        { header: "Check-in", key: "check_in" },
        { header: "Check-out", key: "check_out" },
        { header: "Hours", key: "hours" },
      ];
    case "leave":
      return [
        { header: "Employee", key: "employee_id" },
        { header: "Type", key: "leave_type" },
        { header: "From", key: "start_date" },
        { header: "To", key: "end_date" },
        { header: "Days", key: "days" },
        { header: "Status", cell: r => <Badge variant={r.status === "approved" ? "healthy" : r.status === "rejected" ? "critical" : "pending"}>{r.status}</Badge> },
      ];
    case "training":
      return [
        { header: "Employee", key: "employee_id" },
        { header: "Programme", cell: r => <span className="font-medium">{r.program}</span> },
        { header: "Provider", key: "provider" },
        { header: "Start", key: "start_date" },
        { header: "Status", cell: r => <Badge variant={r.status === "completed" ? "healthy" : "primary"}>{r.status}</Badge> },
      ];
    case "contracts":
      return [
        { header: "Employee", key: "employee_id" },
        { header: "Type", key: "contract_type" },
        { header: "Start", key: "start_date" },
        { header: "End", key: "end_date" },
        { header: "Salary", cell: r => r.salary ? `$${Number(r.salary).toLocaleString()}` : "—" },
        { header: "Status", cell: r => <Badge variant={r.status === "active" ? "healthy" : "draft"}>{r.status}</Badge> },
      ];
    case "performance":
      return [
        { header: "Employee", key: "employee_id" },
        { header: "Review date", key: "review_date" },
        { header: "Period", key: "period" },
        { header: "Rating", cell: r => <Badge variant={Number(r.rating) >= 4 ? "healthy" : Number(r.rating) >= 3 ? "pending" : "critical"}>{r.rating} / 5</Badge> },
        { header: "Reviewer", key: "reviewer" },
      ];
    default: return [];
  }
}

function HRForm({ tab, form, setForm, submit, onCancel }) {
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <form onSubmit={submit} className="rounded-2xl border border-border bg-muted/30 p-5 mb-5 grid grid-cols-1 md:grid-cols-3 gap-3">
      {tab === "employees" && (<>
        <Field label="Full name"><Input value={form.full_name || ""} onChange={e => upd("full_name", e.target.value)} required data-testid="hr-form-full-name" /></Field>
        <Field label="Role title"><Input value={form.role_title || ""} onChange={e => upd("role_title", e.target.value)} required /></Field>
        <Field label="Department"><Input value={form.department || ""} onChange={e => upd("department", e.target.value)} /></Field>
        <Field label="Contact phone"><Input value={form.contact_phone || ""} onChange={e => upd("contact_phone", e.target.value)} /></Field>
        <Field label="Contact email"><Input type="email" value={form.contact_email || ""} onChange={e => upd("contact_email", e.target.value)} /></Field>
        <Field label="Hire date"><Input type="date" value={form.hire_date || ""} onChange={e => upd("hire_date", e.target.value)} required /></Field>
        <Field label="Contract type">
          <Select value={form.contract_type || "full_time"} onChange={e => upd("contract_type", e.target.value)}>
            {["full_time", "part_time", "casual", "contract"].map(v => <option key={v} value={v}>{v.replace("_", " ")}</option>)}
          </Select>
        </Field>
        <Field label="Salary"><Input type="number" value={form.salary || ""} onChange={e => upd("salary", parseFloat(e.target.value))} /></Field>
      </>)}
      {tab === "attendance" && (<>
        <Field label="Employee ID"><Input value={form.employee_id || ""} onChange={e => upd("employee_id", e.target.value)} required /></Field>
        <Field label="Date"><Input type="date" value={form.date || ""} onChange={e => upd("date", e.target.value)} required /></Field>
        <Field label="Status">
          <Select value={form.status || "present"} onChange={e => upd("status", e.target.value)}>
            {["present", "absent", "leave", "half_day"].map(v => <option key={v} value={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Check-in"><Input type="time" value={form.check_in || ""} onChange={e => upd("check_in", e.target.value)} /></Field>
        <Field label="Check-out"><Input type="time" value={form.check_out || ""} onChange={e => upd("check_out", e.target.value)} /></Field>
      </>)}
      {tab === "leave" && (<>
        <Field label="Employee ID"><Input value={form.employee_id || ""} onChange={e => upd("employee_id", e.target.value)} required /></Field>
        <Field label="Leave type">
          <Select value={form.leave_type || "annual"} onChange={e => upd("leave_type", e.target.value)}>
            {["annual", "sick", "maternity", "unpaid", "compassionate"].map(v => <option key={v} value={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Days"><Input type="number" step="0.5" value={form.days || ""} onChange={e => upd("days", parseFloat(e.target.value))} required /></Field>
        <Field label="Start date"><Input type="date" value={form.start_date || ""} onChange={e => upd("start_date", e.target.value)} required /></Field>
        <Field label="End date"><Input type="date" value={form.end_date || ""} onChange={e => upd("end_date", e.target.value)} required /></Field>
        <Field label="Reason" className="md:col-span-3"><Input value={form.reason || ""} onChange={e => upd("reason", e.target.value)} /></Field>
      </>)}
      {tab === "training" && (<>
        <Field label="Employee ID"><Input value={form.employee_id || ""} onChange={e => upd("employee_id", e.target.value)} required /></Field>
        <Field label="Programme"><Input value={form.program || ""} onChange={e => upd("program", e.target.value)} required /></Field>
        <Field label="Provider"><Input value={form.provider || ""} onChange={e => upd("provider", e.target.value)} /></Field>
        <Field label="Start date"><Input type="date" value={form.start_date || ""} onChange={e => upd("start_date", e.target.value)} required /></Field>
        <Field label="End date"><Input type="date" value={form.end_date || ""} onChange={e => upd("end_date", e.target.value)} /></Field>
        <Field label="Cost"><Input type="number" value={form.cost || ""} onChange={e => upd("cost", parseFloat(e.target.value))} /></Field>
      </>)}
      {tab === "contracts" && (<>
        <Field label="Employee ID"><Input value={form.employee_id || ""} onChange={e => upd("employee_id", e.target.value)} required /></Field>
        <Field label="Contract type"><Input value={form.contract_type || ""} onChange={e => upd("contract_type", e.target.value)} required /></Field>
        <Field label="Start date"><Input type="date" value={form.start_date || ""} onChange={e => upd("start_date", e.target.value)} required /></Field>
        <Field label="End date"><Input type="date" value={form.end_date || ""} onChange={e => upd("end_date", e.target.value)} /></Field>
        <Field label="Salary"><Input type="number" value={form.salary || ""} onChange={e => upd("salary", parseFloat(e.target.value))} /></Field>
      </>)}
      {tab === "performance" && (<>
        <Field label="Employee ID"><Input value={form.employee_id || ""} onChange={e => upd("employee_id", e.target.value)} required /></Field>
        <Field label="Review date"><Input type="date" value={form.review_date || ""} onChange={e => upd("review_date", e.target.value)} required /></Field>
        <Field label="Period"><Input value={form.period || ""} onChange={e => upd("period", e.target.value)} required /></Field>
        <Field label="Rating (1-5)"><Input type="number" step="0.1" min="1" max="5" value={form.rating || ""} onChange={e => upd("rating", parseFloat(e.target.value))} required /></Field>
        <Field label="Reviewer"><Input value={form.reviewer || ""} onChange={e => upd("reviewer", e.target.value)} /></Field>
      </>)}
      <div className="md:col-span-3 flex justify-end gap-2 mt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" data-testid="hr-form-submit">Save</Button>
      </div>
    </form>
  );
}
